var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import AdminPageLayout from '../../../layout/admin-page-layout';
import AdAddEdit from './_components/ad-add-edit';
import RadioGroup from '@rad/components/inputs/radio-group';
import Dropdown from '@rad/components/inputs/dropdown';
import Input from '@rad/components/inputs/input';
import ActionButton from '@rad/components/ui/action-button';
import createTable from '@rad/components/ui/table';
import { useFetch, usePromise } from '@rad/hooks';
import { withAllOption } from '@rad/utility/promise';
import { getDistrictAds, createDistrictAd, updateDistrictAd, deleteDistrictAd, getAdLocations } from '../../../common/network/district';
import { uploadFile } from '../../../common/network/attachment';
import { BLOB_BASE_AD } from '../../../common/constants';
import { createAdState } from '../../../types/district';
import { useToast } from '../../../contexts/toast';
import { LocalDate } from '@js-joda/core';
const AdminContentAds = () => {
    const toast = useToast();
    const [adsPromise, refetchAds] = useFetch(getDistrictAds());
    const [locationsPromise] = useFetch(getAdLocations());
    const locations = usePromise(locationsPromise) || [];
    const [showAdModal, setShowAdModal] = React.useState(false);
    const [adForm, setAdForm] = React.useState(createAdState());
    const [adErrors, setAdErrors] = React.useState({});
    const [adFile, setAdFile] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [filterLocation, setFilterLocation] = React.useState('');
    const [activeFilter, setActiveFilter] = React.useState('active');
    const [sort, setSort] = React.useState({ field: 'businessTitle', direction: 'asc' });
    const filteredAdsPromise = React.useMemo(() => adsPromise.then(ads => {
        const today = LocalDate.now();
        return ads
            .filter(ad => activeFilter !== 'active' || isAdActive(ad, today))
            .filter(ad => !searchTerm || ad.businessTitle.toLowerCase().includes(searchTerm.toLowerCase()) || ad.location.toLowerCase().includes(searchTerm.toLowerCase()))
            .filter(ad => !filterLocation || ad.location === filterLocation);
    }), [adsPromise, activeFilter, searchTerm, filterLocation]);
    const AdTable = createTable(filteredAdsPromise);
    const today = LocalDate.now();
    const openAddAdModal = () => {
        setAdForm(createAdState());
        setAdErrors({});
        setAdFile(null);
        setShowAdModal(true);
    };
    const openEditAdModal = (ad) => {
        setAdForm({
            id: ad.id,
            location: ad.location,
            businessTitle: ad.businessTitle,
            adPhotoAttachmentId: ad.adPhotoAttachmentId,
            adBlurb: ad.adBlurb,
            ctaUrl: ad.ctaUrl,
            activeDate: LocalDate.parse(ad.activeDate),
            expirationDate: ad.expirationDate ? LocalDate.parse(ad.expirationDate) : null,
        });
        setAdErrors({});
        setAdFile(null);
        setShowAdModal(true);
    };
    const handleSaveAd = () => __awaiter(void 0, void 0, void 0, function* () {
        setAdErrors({});
        const formToSave = Object.assign({}, adForm);
        if (adFile) {
            const uploaded = yield uploadFile(adFile, 'ad');
            if (uploaded) {
                formToSave.adPhotoAttachmentId = uploaded.id;
            }
            else {
                toast.error('Failed to upload photo');
                return;
            }
        }
        const result = formToSave.id
            ? yield updateDistrictAd(formToSave)
            : yield createDistrictAd(formToSave);
        if (result[0]) {
            setShowAdModal(false);
            refetchAds();
            toast.success(adForm.id ? 'Ad updated' : 'Ad created');
        }
        else {
            const errors = {};
            result[1].forEach(err => {
                errors[err.field] = err.message;
            });
            setAdErrors(errors);
            toast.error('Please fix the errors below');
        }
    });
    const handleDeleteAd = (ad) => __awaiter(void 0, void 0, void 0, function* () {
        if (confirm(`Are you sure you want to delete the ad for ${ad.businessTitle}?`)) {
            yield deleteDistrictAd(ad.id);
            refetchAds();
            toast.success('Ad deleted');
        }
    });
    const isAdActive = (ad, today) => {
        try {
            if (!ad.activeDate)
                return false;
            const activeDate = LocalDate.parse(ad.activeDate);
            const expirationDate = ad.expirationDate ? LocalDate.parse(ad.expirationDate) : null;
            return activeDate.compareTo(today) <= 0 && (!expirationDate || expirationDate.compareTo(today) >= 0);
        }
        catch (_a) {
            return false;
        }
    };
    const getStatusBadge = (ad) => {
        try {
            const activeDate = LocalDate.parse(ad.activeDate);
            const expirationDate = ad.expirationDate ? LocalDate.parse(ad.expirationDate) : null;
            if (activeDate.compareTo(today) > 0)
                return _jsx("span", { className: "badge bg-warning", children: "Scheduled" });
            if (expirationDate && expirationDate.compareTo(today) < 0)
                return _jsx("span", { className: "badge bg-secondary", children: "Expired" });
            return _jsx("span", { className: "badge bg-success", children: "Active" });
        }
        catch (_a) {
            return null;
        }
    };
    const getLocationLabel = (location) => { var _a; return ((_a = locations.find(loc => loc.name === location)) === null || _a === void 0 ? void 0 : _a.title) || location; };
    const adsCount = React.useMemo(() => {
        return adsPromise.then(ads => {
            const today = LocalDate.now();
            const activeCount = ads.filter(ad => isAdActive(ad, today)).length;
            return { active: activeCount, all: ads.length };
        });
    }, [adsPromise]);
    return (_jsx(AdminPageLayout, { children: _jsxs("div", { className: "container py-4", children: [_jsx("h5", { className: "fw-semibold mb-3", children: "Ad Manager" }), _jsxs("div", { className: "d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4", children: [_jsxs("div", { className: "d-flex align-items-center gap-2 flex-wrap", children: [_jsx(RadioGroup, { value: activeFilter, onChange: (val) => setActiveFilter(val || 'active'), options: adsCount.then(counts => [
                                        { value: 'active', label: `Active ${counts.active}` },
                                        { value: 'all', label: `All ${counts.all}` }
                                    ]), optionKey: (opt) => opt.value, optionLabel: (opt) => opt.label, buttonGroupClassName: "btn-group", buttonClassName: (opt) => `btn ${activeFilter === opt.value ? 'btn-primary' : 'btn-outline-secondary'}` }), _jsx("div", { style: { width: 240 }, children: _jsx(Input, { kind: "text", value: searchTerm, onChange: setSearchTerm, label: "Search ads..." }) }), _jsx(Dropdown, { value: filterLocation, onChange: (val) => setFilterLocation(val || ''), options: withAllOption(locationsPromise, { key: 'name', labelKey: 'title', allLabel: 'All locations' }), optionKey: (opt) => opt.name, optionLabel: (opt) => opt.title, label: "Location", style: { width: 200 } })] }), _jsxs("button", { className: "btn btn-primary", onClick: openAddAdModal, children: [_jsx("i", { className: "bi bi-plus-lg me-2" }), "Add New Ad"] })] }), _jsx(AdTable, { className: "table-bordered", sort: sort, onSortChange: setSort, columns: [
                        { header: 'Location', sortField: 'location', render: (ad) => getLocationLabel(ad.location) },
                        { header: 'Business', sortField: 'businessTitle', render: (ad) => _jsx(_Fragment, { children: ad.businessTitle }) },
                        { header: 'Active Date', sortField: 'activeDate', render: (ad) => _jsx(_Fragment, { children: ad.activeDate }) },
                        { header: 'Expiration Date', sortField: 'expirationDate', render: (ad) => _jsx(_Fragment, { children: ad.expirationDate || 'No expiration' }) },
                        ...(activeFilter === 'all' ? [{ header: 'Status', render: getStatusBadge }] : []),
                        { header: 'Actions', className: 'text-center', render: (ad) => _jsxs("div", { onClick: e => e.stopPropagation(), className: "d-inline-flex gap-2", children: [_jsx("button", { className: "btn btn-link text-primary p-0", onClick: () => openEditAdModal(ad), title: "Edit", children: _jsx("i", { className: "bi bi-pencil" }) }), _jsx(ActionButton, { className: "btn btn-link text-danger p-0", onClick: () => handleDeleteAd(ad), title: "Delete", children: _jsx("i", { className: "bi bi-trash" }) })] }) }
                    ] }), _jsx(AdAddEdit, { isOpen: showAdModal, value: adForm, onChange: setAdForm, onCancel: () => setShowAdModal(false), onSave: handleSaveAd, errors: adErrors, locations: locations, file: adFile, onFileChange: setAdFile, currentPhotoUrl: adForm.adPhotoAttachmentId ? `${BLOB_BASE_AD}/${adForm.adPhotoAttachmentId}` : null })] }) }));
};
export default AdminContentAds;
